/**
 * Create Feature Request Modal
 * 
 * Modal for creating a feature REQUEST from the Executive AI Co-Pilot.
 * Creates a request in the inbox (requests collection) for triage,
 * NOT a feature directly.
 * 
 * Uses the unified FileUploader for immediate file uploads.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Loader2, Sparkles } from 'lucide-react';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { requestsApi, API_BASE_URL } from '../../services/api';
import { toast } from 'sonner';
import { MarkdownEditor } from '../ui/markdown-editor';
import FileUploader from '../files/FileUploader';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const PRIORITY_OPTIONS = [
  { value: 'Critical', label: 'Critical', color: 'text-red-600' },
  { value: 'High', label: 'High', color: 'text-orange-600' },
  { value: 'Medium', label: 'Medium', color: 'text-yellow-600' },
  { value: 'Low', label: 'Low', color: 'text-green-600' },
];

const CreateFeatureRequestModal = ({
  open,
  onOpenChange,
  initialData = {},
  onSuccess,
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const { user } = useAuth();
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priorityLevel, setPriorityLevel] = useState('Medium');
  const [productId, setProductId] = useState('');
  
  // File upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [tempBatchId] = useState(() => `temp_request_${Date.now()}`);
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Initialize form with AI-suggested data
  useEffect(() => {
    if (open && initialData) {
      setTitle(initialData.title || '');
      setDescription(initialData.description || '');
      setPriorityLevel(initialData.priority_level || 'Medium');
      setProductId(initialData.product_id || '');
    }
  }, [open, initialData]);
  
  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    if (!productId) {
      toast.error('Product ID is missing from context');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create request data - this goes to the inbox for triage
      const requestData = {
        type: 'feature',  // Type of request (feature, epic, persona, goal, initiative)
        title: title.trim(),
        description: description.trim(),
        tags: [],
        request_data: {
          // Pre-fill data that can be used when promoting to a feature
          priority_level: priorityLevel,
          source: 'executive_ai'
        }
      };
      
      // Create the request in the inbox
      const response = await requestsApi.create(productId, requestData);
      const newRequest = response.data;
      const requestId = newRequest.request_id;
      
      // If there are uploaded files, attach them to the new request
      if (tempBatchId) {
        try {
          const token = localStorage.getItem('session_token');
          await axios.post(
            `${API_BASE_URL}/api/files/attach`,
            {
              temp_batch_id: tempBatchId,
              entity_type: 'request',
              entity_id: requestId
            },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
        } catch (attachErr) {
          console.error('Error attaching files:', attachErr);
          // Don't fail the whole operation if attachment fails
          toast.warning('Request created, but some attachments may not have been linked.');
        }
      }
      
      toast.success('Feature request added to inbox!');
      onOpenChange(false);
      
      if (onSuccess) {
        onSuccess(newRequest);
      }
    } catch (err) {
      console.error('Error creating request:', err);
      toast.error(err.response?.data?.detail || 'Failed to create feature request');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleClose = () => {
    onOpenChange(false);
    // Reset form
    setTitle('');
    setDescription('');
    setPriorityLevel('Medium');
    setProductId('');
    setUploadedFiles([]);
  };
  
  const organizationId = user?.organization_id;
  const canCreate = title.trim() && productId && !isUploading;
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-500" />
            Create Feature Request
          </DialogTitle>
          <DialogDescription>
            This will add a feature request to the inbox for triage. You can review and promote it to a feature later.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Title and Priority - Same row */}
          <div className="flex gap-4">
            <div className="space-y-2 flex-[0.7]">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Feature request title"
                data-testid="feature-request-title"
              />
            </div>
            
            <div className="space-y-2 flex-[0.3]">
              <Label>Priority</Label>
              <Select value={priorityLevel} onValueChange={setPriorityLevel}>
                <SelectTrigger data-testid="feature-request-priority">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <span className={option.color}>{option.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <MarkdownEditor
              content={description}
              onChange={setDescription}
              placeholder="Describe the feature request..."
              minHeight={150}
            />
          </div>
          
          {/* File Attachments - Using unified FileUploader */}
          <div className="space-y-2">
            <Label>Attachments</Label>
            {productId && organizationId ? (
              <FileUploader
                organizationId={organizationId}
                productId={productId}
                entityType="request"
                tempBatchId={tempBatchId}
                onUploadingChange={setIsUploading}
                onFilesChange={setUploadedFiles}
                maxFiles={10}
                maxSizeMB={50}
                disabled={isSubmitting}
              />
            ) : (
              <div className="text-sm text-muted-foreground p-4 border border-dashed rounded-lg text-center">
                Product context required for file uploads
              </div>
            )}
          </div>
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {isUploading && (
            <span className="text-sm text-muted-foreground mr-auto">
              Uploading files...
            </span>
          )}
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={isSubmitting || !canCreate}
              data-testid="feature-request-submit"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add to Inbox
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateFeatureRequestModal;
